import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  Camera,
  Image as ImageIcon,
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileText,
  ImageOff,
  WifiOff,
  Clock,
  PenLine,
  Plus,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { receiptsApi } from '../api/receipts';
import { toast } from '../components/ui/Toast';
import type { Receipt } from '../types';
import { toFixed, formatEUR } from '../utils/format';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { addPendingUpload, getPendingUploadCount } from '../lib/offlineDb';
import { ReceiptScanner } from '../components/ReceiptScanner';

type UploadStatus = 'idle' | 'uploading' | 'processing' | 'done' | 'error' | 'queued';

const MAX_IMAGES_PER_RECEIPT = 5;

/** Prueft ob getUserMedia im aktuellen Browser verfuegbar ist */
function isGetUserMediaSupported(): boolean {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

function fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [scannerOpen, setScannerOpen] = useState(false);

  const isOnline = useOnlineStatus();

  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [receiptImageUrl, setReceiptImageUrl] = useState<string | null>(null);
  const [receiptImageLoading, setReceiptImageLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);
  const redirectTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Anzahl ausstehender Uploads laden
  useEffect(() => {
    getPendingUploadCount().then(setPendingCount).catch(() => setPendingCount(0));
  }, [status]);

  /** Validate a single image and return null on success or an error message. */
  const validateFile = (f: File): string | null => {
    if (!f.type.startsWith('image/')) return 'Bitte wähle ein Bild (JPEG, PNG, WebP)';
    if (f.size > 10 * 1024 * 1024) return 'Maximale Dateigröße pro Bild: 10 MB';
    return null;
  };

  /** Add 1..n new files. Replaces the queue if a single file is added in idle
   *  mode AND no existing files — i.e. a fresh start; otherwise appends. */
  const addFiles = useCallback(async (newFiles: File[]) => {
    const validated: File[] = [];
    for (const f of newFiles) {
      const err = validateFile(f);
      if (err) {
        toast.error('Ungültige Datei', `${f.name}: ${err}`);
        continue;
      }
      validated.push(f);
    }
    if (validated.length === 0) return;

    setFiles((current) => {
      const merged = [...current, ...validated].slice(0, MAX_IMAGES_PER_RECEIPT);
      if (current.length + validated.length > MAX_IMAGES_PER_RECEIPT) {
        toast.info('Maximum erreicht', `Max. ${MAX_IMAGES_PER_RECEIPT} Bilder pro Beleg`);
      }
      return merged;
    });
    setStatus('idle');
    setReceipt(null);

    const newPreviews = await Promise.all(validated.map(fileToDataURL));
    setPreviews((current) => [...current, ...newPreviews].slice(0, MAX_IMAGES_PER_RECEIPT));
  }, []);

  /** Single-file handler kept for camera scanner that always returns one file. */
  const handleFile = useCallback((selectedFile: File) => {
    void addFiles([selectedFile]);
  }, [addFiles]);

  const removeFileAt = (index: number) => {
    setFiles((current) => current.filter((_, i) => i !== index));
    setPreviews((current) => current.filter((_, i) => i !== index));
  };

  const moveFile = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0) return;
    setFiles((current) => {
      if (target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setPreviews((current) => {
      if (target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        void addFiles(Array.from(e.dataTransfer.files));
      }
    },
    [addFiles]
  );

  const handleUpload = async () => {
    if (files.length === 0) return;

    // Offline: only single-image uploads are queueable for now.
    if (!isOnline) {
      if (files.length > 1) {
        toast.error('Offline', 'Mehrere Bilder können offline nicht gespeichert werden.');
        return;
      }
      try {
        const single = files[0];
        const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        await addPendingUpload(pendingId, single, single.name);
        setStatus('queued');
        const newCount = await getPendingUploadCount();
        setPendingCount(newCount);
        toast.info(
          'Beleg gespeichert',
          'Wird hochgeladen, sobald du online bist.'
        );
      } catch {
        toast.error('Fehler', 'Beleg konnte nicht gespeichert werden.');
      }
      return;
    }

    setStatus('uploading');
    try {
      const uploadedReceipt = files.length === 1
        ? await receiptsApi.upload(files[0])
        : await receiptsApi.uploadMulti(files);
      setStatus('processing');

      // Poll for OCR completion
      pollIntervalRef.current = setInterval(async () => {
        try {
          const updated = await receiptsApi.get(uploadedReceipt.id);
          if (updated.status === 'ocr_done' || updated.status === 'completed' || updated.status === 'reviewed') {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
            setReceipt(updated);
            setStatus('done');
            toast.success('Fertig!', 'Kassenzettel wurde erfolgreich verarbeitet.');
          } else if (updated.status === 'error') {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
            setReceipt(updated);
            setStatus('error');
            toast.error('OCR-Fehler', 'Die Texterkennung ist fehlgeschlagen.');
          }
        } catch {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
          setStatus('error');
        }
      }, 2000);

      // Safety timeout after 60 seconds
      pollTimeoutRef.current = setTimeout(() => {
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        setStatus((current) => {
          if (current === 'processing') {
            setReceipt(uploadedReceipt);
            toast.info('Verarbeitung läuft', 'Die OCR-Erkennung wird im Hintergrund fortgesetzt.');
            return 'done';
          }
          return current;
        });
      }, 60000);
    } catch {
      setStatus('error');
      toast.error('Upload fehlgeschlagen', 'Bitte versuche es erneut.');
    }
  };

  // Cleanup interval/timeout on unmount to prevent memory leak
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      if (redirectTimerRef.current) clearInterval(redirectTimerRef.current);
    };
  }, []);

  // Auto-redirect countdown after successful OCR
  useEffect(() => {
    if (status !== 'done' || !receipt?.id) return;

    setRedirectCountdown(2);
    let count = 2;
    redirectTimerRef.current = setInterval(() => {
      count -= 1;
      if (count <= 0) {
        if (redirectTimerRef.current) clearInterval(redirectTimerRef.current);
        navigate(`/receipts/${receipt.id}`);
      } else {
        setRedirectCountdown(count);
      }
    }, 1000);

    return () => {
      if (redirectTimerRef.current) clearInterval(redirectTimerRef.current);
      setRedirectCountdown(null);
    };
  }, [status, receipt?.id, navigate]);

  // Load receipt image from server when upload is done
  useEffect(() => {
    if (!receipt?.id || status !== 'done') return;

    let cancelled = false;
    setReceiptImageLoading(true);

    receiptsApi.fetchImageBlob(receipt.id, 'thumb').then((blob) => {
      if (!cancelled) {
        setReceiptImageUrl(URL.createObjectURL(blob));
        setReceiptImageLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setReceiptImageLoading(false);
      }
    });

    return () => {
      cancelled = true;
      setReceiptImageUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [receipt?.id, status]);

  const resetUpload = () => {
    setFiles([]);
    setPreviews([]);
    setStatus('idle');
    setReceipt(null);
    setReceiptImageUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setReceiptImageLoading(false);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-800 dark:text-white">Beleg hinzufügen</h1>
        <p className="text-neutral-500 dark:text-neutral-400 mt-1">
          Fotografiere oder gib den Beleg manuell ein
        </p>
      </div>

      {/* Tab-Toggle: Foto | Manuell */}
      <div className="flex gap-2 p-1 rounded-xl bg-neutral-100 dark:bg-white/5 border border-neutral-200/80 dark:border-indigo-500/10">
        <span
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold
            bg-white dark:bg-[#1a1a2e] text-primary-600 dark:text-primary-400
            shadow-sm border border-neutral-200/60 dark:border-indigo-500/20"
          aria-current="page"
        >
          <Camera className="w-4 h-4" aria-hidden="true" />
          Foto
        </span>
        <button
          type="button"
          onClick={() => navigate('/quick-entry')}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold
            text-neutral-500 dark:text-neutral-400
            hover:bg-white/60 dark:hover:bg-white/5 hover:text-neutral-700 dark:hover:text-neutral-200
            transition-all duration-150"
        >
          <PenLine className="w-4 h-4" aria-hidden="true" />
          Manuell
        </button>
      </div>

      {/* Hinweis: Ausstehende Uploads */}
      {pendingCount > 0 && (
        <div
          role="status"
          className="flex items-center gap-3 px-4 py-3 rounded-xl bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/20 text-yellow-800 dark:text-yellow-300"
        >
          <Clock className="w-4 h-4 shrink-0" aria-hidden="true" />
          <p className="text-sm font-medium">
            {pendingCount === 1
              ? '1 Beleg wartet auf Synchronisierung.'
              : `${pendingCount} Belege warten auf Synchronisierung.`}
          </p>
        </div>
      )}

      {/* Upload Area */}
      {previews.length === 0 && (
        <Card padding="none">
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`
              relative flex flex-col items-center justify-center p-12 rounded-2xl
              border-2 border-dashed transition-all duration-200 cursor-pointer
              ${
                dragActive
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10'
                  : 'border-neutral-300 dark:border-indigo-500/20 hover:border-primary-400 hover:bg-neutral-50 dark:hover:bg-white/5'
              }
            `}
            onClick={() => {
              // Only open file picker on desktop (not mobile, where buttons are used)
              if (window.innerWidth >= 640) fileInputRef.current?.click();
            }}
          >
            <div className="w-16 h-16 bg-primary-50 rounded-2xl flex items-center justify-center mb-4">
              <Upload className="w-8 h-8 text-primary-500" />
            </div>
            <p className="text-base font-semibold text-neutral-700 dark:text-neutral-200 mb-1">
              Bild hier ablegen
            </p>
            <p className="text-sm text-neutral-400 dark:text-neutral-500 mb-6">
              oder wähle eine Option (JPEG, PNG, WebP, max. 10 MB)
            </p>

            <div className="flex gap-3">
              <Button
                variant="outline"
                icon={<ImageIcon className="w-4 h-4" />}
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                Aus Galerie
              </Button>
              <Button
                variant="primary"
                icon={<Camera className="w-4 h-4" />}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isGetUserMediaSupported()) {
                    // Moderner Scanner mit Vorverarbeitung
                    setScannerOpen(true);
                  } else {
                    // Fallback: nativer Datei-Input mit capture
                    cameraInputRef.current?.click();
                  }
                }}
              >
                Kamera
              </Button>
            </div>

            {/* File picker: multiple = select up to 5 photos for one long receipt */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  void addFiles(Array.from(e.target.files));
                }
                e.target.value = '';
              }}
            />
            {/* Camera: capture="environment" = opens camera directly on mobile */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) handleFile(e.target.files[0]);
                e.target.value = '';
              }}
            />
          </div>
        </Card>
      )}

      {/* Preview + Status */}
      {previews.length > 0 && (
        <Card padding="lg">
          <div className="space-y-6">
            {/* Image Preview Grid */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                  {files.length === 1
                    ? '1 Bild ausgewählt'
                    : `${files.length} Bilder — Reihenfolge: oben → unten`}
                </p>
                {status === 'idle' && files.length < MAX_IMAGES_PER_RECEIPT && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<Plus className="w-4 h-4" />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Weiteres Bild
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {previews.map((src, idx) => (
                  <div
                    key={`${idx}-${src.slice(-30)}`}
                    className="relative aspect-[3/4] rounded-xl border border-neutral-200 dark:border-indigo-500/20 overflow-hidden bg-neutral-50 dark:bg-white/5"
                  >
                    <img
                      src={src}
                      alt={`Bild ${idx + 1} von ${previews.length}`}
                      className="w-full h-full object-cover"
                    />
                    {/* Order badge */}
                    <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-md bg-black/60 text-white text-[11px] font-semibold tabular-nums">
                      {idx + 1}
                    </span>
                    {/* Reorder + remove (only while idle) */}
                    {status === 'idle' && (
                      <>
                        {idx > 0 && (
                          <button
                            type="button"
                            onClick={() => moveFile(idx, -1)}
                            className="absolute top-1 right-7 w-6 h-6 rounded-md bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
                            aria-label="Nach oben verschieben"
                          >
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {idx < previews.length - 1 && (
                          <button
                            type="button"
                            onClick={() => moveFile(idx, 1)}
                            className="absolute bottom-1 right-7 w-6 h-6 rounded-md bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
                            aria-label="Nach unten verschieben"
                          >
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeFileAt(idx)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-md bg-black/60 hover:bg-error-500 text-white flex items-center justify-center"
                          aria-label="Bild entfernen"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Info + Actions */}
            <div className="space-y-4">
              {/* Total size */}
              <div>
                <p className="text-xs text-neutral-400">
                  {(files.reduce((acc, f) => acc + f.size, 0) / 1024 / 1024).toFixed(2)} MB gesamt
                  {files.length > 1 && ' — werden zu einem Beleg zusammengeführt'}
                </p>
              </div>

              {/* Status */}
              <div className="flex items-center gap-3">
                {status === 'idle' && (
                  <span className="text-sm text-neutral-500">
                    {isOnline ? 'Bereit zum Hochladen' : 'Bereit — wird offline gespeichert'}
                  </span>
                )}
                {status === 'uploading' && (
                  <div className="flex items-center gap-2 text-sm text-primary-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Wird hochgeladen...
                  </div>
                )}
                {status === 'processing' && (
                  <div className="flex items-center gap-2 text-sm text-secondary-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    OCR-Erkennung laeuft...
                  </div>
                )}
                {status === 'done' && (
                  <div className="flex items-center gap-2 text-sm text-success-500">
                    <CheckCircle className="w-4 h-4" />
                    Erfolgreich verarbeitet
                  </div>
                )}
                {status === 'queued' && (
                  <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400">
                    <WifiOff className="w-4 h-4" />
                    Gespeichert — wird synchronisiert, wenn du online bist
                  </div>
                )}
                {status === 'error' && (
                  <div className="flex items-center gap-2 text-sm text-error-500">
                    <AlertCircle className="w-4 h-4" />
                    Fehler bei der Verarbeitung
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                {status === 'idle' && (
                  <>
                    <Button onClick={handleUpload}>
                      <Upload className="w-4 h-4 mr-1.5" />
                      Hochladen
                    </Button>
                    <Button variant="ghost" onClick={resetUpload}>
                      Abbrechen
                    </Button>
                  </>
                )}
                {status === 'done' && receipt && (
                  <>
                    <div className="flex flex-col gap-2 w-full">
                      {redirectCountdown !== null && (
                        <p className="text-xs text-neutral-400 dark:text-neutral-500">
                          Weiterleitung in {redirectCountdown}s...
                        </p>
                      )}
                      <div className="flex gap-3">
                        <Button onClick={() => {
                          if (redirectTimerRef.current) clearInterval(redirectTimerRef.current);
                          setRedirectCountdown(null);
                          navigate(`/receipts/${receipt.id}`);
                        }}>
                          <FileText className="w-4 h-4 mr-1.5" />
                          Jetzt anzeigen
                        </Button>
                        <Button variant="ghost" onClick={() => {
                          if (redirectTimerRef.current) clearInterval(redirectTimerRef.current);
                          setRedirectCountdown(null);
                          resetUpload();
                        }}>
                          Weiteren hochladen
                        </Button>
                      </div>
                    </div>
                  </>
                )}
                {status === 'queued' && (
                  <Button variant="outline" onClick={resetUpload}>
                    Weiteren hinzufuegen
                  </Button>
                )}
                {status === 'error' && (
                  <>
                    <Button onClick={handleUpload}>
                      Erneut versuchen
                    </Button>
                    <Button variant="ghost" onClick={resetUpload}>
                      Abbrechen
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ReceiptScanner Overlay */}
      {scannerOpen && (
        <ReceiptScanner
          onCapture={(capturedFile) => {
            setScannerOpen(false);
            handleFile(capturedFile);
          }}
          onClose={() => setScannerOpen(false)}
        />
      )}

      {/* OCR Results Preview */}
      {status === 'done' && receipt && (
        <Card padding="lg">
          <h2 className="text-lg font-semibold text-neutral-800 dark:text-white mb-4">
            Erkannte Daten
          </h2>

          {/* Server-side receipt image */}
          <div className="mb-6">
            <div className="w-full max-w-xs mx-auto aspect-[3/4] bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center overflow-hidden">
              {receiptImageLoading && (
                <div className="flex flex-col items-center text-neutral-400">
                  <Loader2 className="w-6 h-6 animate-spin mb-1" />
                  <span className="text-xs">Bild wird geladen...</span>
                </div>
              )}
              {!receiptImageLoading && receiptImageUrl && (
                <img
                  src={receiptImageUrl}
                  alt="Kassenzettel"
                  className="w-full h-full object-contain"
                />
              )}
              {!receiptImageLoading && !receiptImageUrl && (
                <div className="flex flex-col items-center text-neutral-400">
                  <ImageOff className="w-8 h-8 mb-1" />
                  <span className="text-xs">Bild nicht verfügbar</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-1">Geschäft</p>
              <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                {receipt.store?.name || receipt.store_name || 'Nicht erkannt'}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-1">Datum</p>
              <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                {receipt.receipt_date
                  ? new Date(receipt.receipt_date).toLocaleDateString('de-DE')
                  : 'Nicht erkannt'}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-1">Gesamtbetrag</p>
              <p className="text-lg font-bold font-mono tabular-nums text-neutral-800 dark:text-neutral-100">
                {receipt.total_amount != null
                  ? formatEUR(receipt.total_amount)
                  : 'Nicht erkannt'}
              </p>
            </div>
            {receipt.ocr_confidence != null && Number(receipt.ocr_confidence) < 60 && (
              <div className="sm:col-span-2">
                <p className="text-sm text-warning-700 dark:text-warning-400 bg-warning-50 dark:bg-warning-500/10
                               px-3 py-2 rounded-lg border border-warning-200 dark:border-warning-500/20">
                  Erkennung unsicher — bitte Daten prüfen
                </p>
              </div>
            )}
          </div>

          {/* Items Preview */}
          {receipt.items && receipt.items.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">
                Erkannte Positionen ({receipt.items.length})
              </h3>
              <div className="space-y-2">
                {receipt.items.map((item, index) => (
                  <div
                    key={item.id || index}
                    className="flex items-center justify-between py-2 px-3 rounded-lg bg-neutral-50 dark:bg-white/5"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-neutral-700 dark:text-neutral-200 truncate">{item.name}</p>
                      <p className="text-xs text-neutral-400">
                        {item.quantity} x {toFixed(item.unit_price, 2)} EUR
                      </p>
                    </div>
                    <p className="text-sm font-mono font-medium tabular-nums text-neutral-800 dark:text-neutral-100">
                      {toFixed(item.total_price, 2)} EUR
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};
